
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ProjectRrwebSession {
  id: string;
  project_id: string;
  rrweb_session_id: string;
  user_id: string;
  order_in_project?: number;
  created_at: string;
}

export const useProjectRrwebSessions = (projectId: string) => {
  return useQuery({
    queryKey: ['project-rrweb-sessions', projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_rrweb_sessions')
        .select('*')
        .eq('project_id', projectId)
        .order('order_in_project', { ascending: true });

      if (error) {
        console.error('Error fetching project rrweb sessions:', error);
        throw error;
      }

      return data as ProjectRrwebSession[];
    },
    enabled: !!projectId,
  });
};

export const useCreateProjectRrwebSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionData: {
      project_id: string;
      rrweb_session_id: string;
      order_in_project?: number;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('project_rrweb_sessions')
        .insert([
          {
            ...sessionData,
            user_id: user.id,
          },
        ])
        .select()
        .single();

      if (error) {
        console.error('Error creating project rrweb session:', error);
        throw error;
      }

      return data as ProjectRrwebSession;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['project-rrweb-sessions', data.project_id] });
      toast.success('Session added to project successfully!');
    },
    onError: (error) => {
      console.error('Failed to add session to project:', error);
      toast.error('Failed to add session to project');
    },
  });
};
